
import Layout from "@/components/layout/Layout";
import OverviewSection from "@/components/dashboard/OverviewSection";
import ChartSection from "@/components/dashboard/ChartSection";
import AlertSection from "@/components/dashboard/AlertSection";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ActivitySection from "@/components/dashboard/ActivitySection";
import { Button } from "@/components/ui/button";
import { CalendarDays, FileBarChart2, LayoutDashboard, RefreshCw } from "lucide-react";
import StatusCard from "@/components/dashboard/StatusCard";
import { Package, Truck, ListChecks } from "lucide-react"; // Changed TruckDelivery to Truck

const Index = () => {
  const formatDate = () => {
    const options: Intl.DateTimeFormatOptions = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return new Date().toLocaleDateString('tr-TR', options);
  };

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground mt-1">{formatDate()}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="h-9">
              <RefreshCw className="mr-2 h-4 w-4" />
              Yenile
            </Button>
            <Button className="h-9">
              <FileBarChart2 className="mr-2 h-4 w-4" />
              Rapor Al
            </Button>
          </div>
        </div>
        
        {/* Dashboard Content */}
        <Tabs defaultValue="overview" className="space-y-6">
          <div className="flex justify-between items-center">
            <TabsList className="grid w-full sm:w-auto grid-cols-2 sm:grid-cols-3 gap-1">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <LayoutDashboard className="h-4 w-4" />
                <span className="hidden sm:inline">Genel Bakış</span>
                <span className="sm:hidden">Genel</span>
              </TabsTrigger>
              <TabsTrigger value="daily" className="flex items-center gap-2">
                <CalendarDays className="h-4 w-4" />
                <span className="hidden sm:inline">Günlük Özet</span>
                <span className="sm:hidden">Günlük</span>
              </TabsTrigger>
              <TabsTrigger value="analytics" className="flex items-center gap-2 col-span-2 sm:col-span-1">
                <FileBarChart2 className="h-4 w-4" />
                <span className="hidden sm:inline">Analitik</span>
                <span className="sm:hidden">Analitik</span>
              </TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="overview" className="space-y-6">
            <OverviewSection />
            <ChartSection />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
              <div className="lg:col-span-2">
                <AlertSection />
              </div>
              <ActivitySection />
            </div>
          </TabsContent>
          
          <TabsContent value="daily" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Günlük Özet</CardTitle>
                <CardDescription>Bugünün önemli bilgileri ve görevleri</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <StatusCard 
                    title="Bekleyen Sevkiyatlar"
                    status="pending"
                    icon={<Truck className="h-5 w-5" />} // Changed from TruckDelivery to Truck
                    description="3 sevkiyat bugün için planlandı"
                    items={[
                      { id: "s1", title: "Hatay Yardım Paketi", description: "15:30 - 100 Koli" },
                      { id: "s2", title: "İzmir İlaç Sevkiyatı", description: "13:45 - 25 Koli" },
                      { id: "s3", title: "Adana Gıda Yardımı", description: "16:00 - 80 Koli" }
                    ]}
                    action={{
                      label: "Sevkiyat Detayları",
                      onClick: () => console.log("Shipment details clicked")
                    }}
                  />
                  
                  <StatusCard 
                    title="Stok Girişleri"
                    status="processing"
                    icon={<Package className="h-5 w-5" />}
                    description="2 stok girişi işleniyor"
                    items={[
                      { id: "i1", title: "Ankara'dan Gelen Yardım", description: "Kabul aşamasında" },
                      { id: "i2", title: "Medikal Malzeme Girişi", description: "Kontrolde" }
                    ]}
                    action={{
                      label: "Tümünü Yönet",
                      onClick: () => console.log("Manage all clicked")
                    }}
                  />
                  
                  <StatusCard 
                    title="Görevler"
                    status="warning"
                    icon={<ListChecks className="h-5 w-5" />}
                    description="4 görev son teslim tarihi yaklaşıyor"
                    items={[
                      { id: "t1", title: "Haftalık Rapor", description: "Bugün son gün" },
                      { id: "t2", title: "Depo Sayımı", description: "Yarın son gün" },
                      { id: "t3", title: "Bakım Kontrolü", description: "2 gün içinde" },
                      { id: "t4", title: "Tedarikçi Görüşmesi", description: "3 gün içinde" }
                    ]}
                    action={{
                      label: "Görev Listesi",
                      onClick: () => console.log("Task list clicked")
                    }}
                  />
                </div>
              </CardContent>
            </Card>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="lg:col-span-2">
                <ChartSection />
              </div>
              <ActivitySection />
            </div>
          </TabsContent>
          
          <TabsContent value="analytics" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Analitik Gösterge Paneli</CardTitle>
                <CardDescription>Detaylı performans ve trend analizi</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">Bu bölüm, detaylı analitik grafikleri ve raporları içerecektir. Şu anda geliştirme aşamasındadır.</p>
              </CardContent>
            </Card>
            
            <ChartSection />
            <AlertSection />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Index;
